<script lang="ts" setup>

import type { NavigationMenuItem } from '@nuxt/ui'

// Currency Store
const currencyStore = useCurrencyStore()
const { currency } = storeToRefs(currencyStore)

// Device
const deviceStore = useDeviceStore()
const isMobile = deviceStore.isMobile

// Idioma
const { locale, t } = useI18n()

const selectedIdioma = computed(() => locale.value)

// Datos Estáticos del Header (Estructura en código, textos en i18n)
const contentStore = useContentStore()
const { suggestedDestinations, support } = storeToRefs(contentStore)
await contentStore.load()

const buttonDestinos = computed(() => ({
  label: t('strapi.global.header.buttonDestinos.label'),
  link: 'destinations',
  url: false
}))

const buttonFunciona = computed(() => ({
  label: t('strapi.global.header.buttonFunciona.label'),
  link: 'como-funciona',
  url: false
}))

const buttonAyuda = computed(() => [
  { label: t('strapi.global.header.buttonAyuda.contacto'), link: support.value?.whatsapp ?? '', url: true },
  { label: t('strapi.global.header.buttonAyuda.preguntas'), link: `https://byhours-support.gorgias.help/${locale.value}-${countryLangCodes[locale.value]}`, url: true }
])

// Helper para construir URLs
const getButtonUrl = (button: { link: string, url?: boolean }) => {
  if (button.url) {
    return button.link
  }
  return `/${locale.value}/${button.link}`
}

// Monedas (estado global desde app.vue)
const currencies: Ref<Currency[]> = useState('currencies', () => [])

// Menu
const open = ref(false)
const openFunciona = ref(false)
const active = ref()

// Cerrar modal al seleccionar idioma o moneda
watch([locale, currency], () => {
  if (open.value) open.value = false
})

const items = computed<NavigationMenuItem[]>(() => [
  {
    label: t('strapi.global.header.labelDestinos'),
    icon: 'i-lucide-map',
    slot: 'destinos',
    children: suggestedDestinations.value?.slice(0, 8).map((destino: SuggestedDestination) => ({
      label: destino.name,
      to: `/${locale.value}/search?data-destinationId=${destino.identifier}`,
      onSelect: () => {
        open.value = false
      }
    })),
  },
  ...(isMobile ? [{
    label: t('buttons.mapa'),
    icon: 'i-lucide-globe',
    to: getButtonUrl(buttonDestinos.value),
    onSelect: () => {
      open.value = false
    }
  }] : []),
  {
    label: buttonFunciona.value.label,
    icon: 'i-lucide-chart-pie',
    to: `/${locale.value}/como-funciona.html`,
    open: openFunciona.value,
    onSelect: () => {
      open.value = false
    }
  },
  {
    label: t('strapi.global.header.buttonPartner.hoteleros'),
    icon: 'i-lucide-building-2',
    to: `/${locale.value}/hoteliers`,
    onSelect: () => {
      open.value = false
    }
  },
  {
    label: t('strapi.global.header.labelAyuda'),
    icon: 'i-lucide-help-circle',
    slot: 'ayuda',
    children: buttonAyuda.value.map((opcion) => ({
      label: opcion.label,
      to: opcion.link,
      target: '_blank',
      onSelect: () => {
        open.value = false
      }
    }))
  }
])

// Scroll Store
const scroll = useScrollStore()
const { isScrolledInput, isScrolled } = storeToRefs(scroll)

const isTransparent = computed(() => isHome.value && !isScrolled.value)

// Home Store
const { isHome } = useIsHome()

// Buscador: visible al hacer scroll en home o en cualquier otra página
const showSearchInput = computed(() => (isScrolledInput.value && isHome.value) || !isHome.value)

// Próxima reserva / repetir reciente (ocupa el hueco del buscador cuando este está oculto)
const { hasBookingWidget } = useBookingWidget()

// User data
const { user, loggedIn } = useUserSession()
const authStore = useAuthStore()

async function logoutAndCloseModal() {
  await authStore.clearAllUserClientState()
  open.value = false
}

const menuItems = computed(() => [
  [
    {
      name: (user.value as OAuthUser)?.name,
      label: (user.value as OAuthUser)?.email,
      img: (user.value as OAuthUser)?.img,
      slot: 'account',
      onClick: () => {
        navigateTo(`/${locale.value}/user-zone/bookings`)
      }
    }
  ],
  [
    {
      label: t('labels.user_zone.menu.reservas'),
      icon: 'i-lucide-building-2',
      onClick: () => {
        navigateTo(`/${locale.value}/user-zone/bookings`)
      }
    },
    {
      label: t('labels.user_zone.menu.favoritos'),
      icon: 'i-heroicons-heart',
      onClick: () => {
        navigateTo(`/${locale.value}/user-zone/favorites`)
      }
    },
    {
      label: t('labels.user_zone.menu.perfil'),
      icon: 'i-lucide-user-circle',
      onClick: () => {
        navigateTo(`/${locale.value}/user-zone/profile`)
      }
    }
  ],
  [
    {
      label: t('labels.login.dropdown.cerrar'),
      icon: 'i-lucide-log-out',
      onClick: () => {
        void authStore.clearAllUserClientState()
      }
    }
  ]
])

function generateUsername(email: string): string {
  if (!email?.includes("@")) {
    return "";
  }

  const [localPart] = email.split("@");
  return localPart?.replace(/[^a-zA-Z0-9]/g, "_") || "";
}

</script>

<template>
  <header class="top-0 left-0 right-0 w-full h-16 z-50 transition-all duration-300 ease-in-out sticky" :class="[
    isTransparent ? 'bg-transparent text-white' : 'bg-white/80 backdrop-blur-lg shadow-xs text-neutral-900'
  ]">
    <div class="px-5 py-4 mx-auto flex justify-between items-center h-full max-w-screen-2xl">
      <div class="flex items-center w-full">

        <!-- Logo -->
        <NuxtLink :aria-label="$t('labels.aria.home')" :to="`/${locale}`">
          <Logo />
        </NuxtLink>

        <!-- Inputs -->
        <div v-if="showSearchInput" class="w-full min-w-0 px-3 sm:flex xl:flex">
          <SearchInput />
        </div>

        <!-- Próxima reserva: aprovecha el hueco del buscador cuando este está oculto -->
        <div v-else-if="hasBookingWidget" class="w-full min-w-0 px-3">
          <NextBookingLink :transparent="isTransparent" />
        </div>
      </div>

      <div class="gap-3 items-center justify-end hidden xl:flex" :class="{ 'w-7/12': isScrolledInput && isHome }">
        <UNavigationMenu :key="locale" color="neutral" v-model="active"
          content-orientation="vertical" :items="items" class="w-full justify-center" :ui="{
            link: [
              'text-xs py-2 transition-all duration-300',
              isTransparent ? 'text-white hover:text-black' : 'text-neutral-800 hover:text-black font-medium'
            ],
            linkLeadingIcon: [
              'size-4 transition-all duration-300',
              isTransparent ? 'text-white' : 'text-neutral-500'
            ],
            content: 'w-fit text-black',
          }">
          <template #destinos-content="{ item }: { item: NavigationMenuItem }">
            <div class="p-5 w-92">
              <p class="text-sm font-bold mb-2 text-neutral-500 pl-2">{{
                t('strapi.global.header.labelDestinos').toUpperCase() }}
              </p>
              <ul class="grid lg:grid-cols-2">
                <li v-for="child in item.children" :key="child.label">
                  <UButton variant="link" :to="child.to"
                    class="text-sm text-left rounded-md p-2 px-4 transition-colors hover:bg-elevated/50 w-full"
                    @click="() => { active = '' }">
                    <p class="font-medium text-toned">
                      {{ child.label }}
                    </p>
                  </UButton>
                </li>
              </ul>
              <UButton block variant="outline" class="mt-5" color="neutral" :to="getButtonUrl(buttonDestinos)"
                target="_self" :aria-label="buttonDestinos.label" size="lg" :label="buttonDestinos.label"
                @click="() => { active = '' }" />
            </div>
          </template>

        </UNavigationMenu>

        <!-- Idioma y moneda -->
        <UPopover>
          <UButton size="xs" :aria-label="$t('labels.aria.idiomaMoneda')" variant="link"
            :class="isTransparent ? 'text-white hover:text-white/80' : 'text-neutral-800 hover:text-black'"
            :label="`${selectedIdioma.toUpperCase()} | ${currency}`" />
          <template #content>
            <div class="flex flex-col gap-2 p-5 pt-3">
              <div>
                <p class="text-neutral-500 text-sm mb-1">{{ t('strapi.global.header.tituloIdioma') }}</p>
                <LocaleSelector size="md" />
              </div>
              <div>
                <p class="text-neutral-500 text-sm mb-1">{{ t('strapi.global.header.tituloMoneda') }}</p>
                <div>
                  <USelect :content="{ bodyLock: false }" class="w-full" size="md" v-model="currency"
                    :items="currencies" labelKey="id" value-key="id" />
                </div>
              </div>
            </div>
          </template>
        </UPopover>

        <!-- Sesión -->
        <AuthState v-slot="{ user, loggedIn }">
          <!-- Logged in -->
          <UDropdownMenu v-if="loggedIn" :items="menuItems">
            <UUser :name="(user as OAuthUser)?.name || generateUsername((user as OAuthUser)?.email)" :avatar="{
              src: (user as OAuthUser)?.img,
              alt: `${(user as OAuthUser)?.name || ''} ${(user as OAuthUser)?.surname || ''}`,
              icon: !(user as OAuthUser)?.name ? 'i-lucide-user-circle' : undefined,
              ui: {
                root: 'bg-byhours-gradient text-[8px]',
                fallback: 'text-white',
                icon: 'text-white text-sm',
              }
            }" class="p-1 pr-2 border border-neutral-300 rounded-full hover:cursor-pointer bg-gray-100 shadow-xs"
              size="2xs" />
            <template #account="{ item }">
              <UUser :name="item.name" :description="item.label" :avatar="{
                src: (user as OAuthUser)?.img,
                alt: `${(user as OAuthUser)?.name || ''} ${(user as OAuthUser)?.surname || ''}`,
                icon: !(user as OAuthUser)?.name ? 'i-lucide-user-circle' : undefined,
                size: 'md',
                ui: {
                  root: 'bg-byhours-gradient text-xs',
                  fallback: 'text-white',
                  icon: 'text-white text-xl',
                }
              }" size="sm" class="text-left truncate" />
            </template>
          </UDropdownMenu>

          <!-- Not logged -->
          <UButton v-else dynamic :to="`/${locale}/login`" :aria-label="$t('labels.aria.usuario')"
            icon="i-lucide-user-circle" :label="$t('buttons.login')" size="xs"
            :variant="isTransparent ? 'outline' : 'subtle'" color="neutral" />
        </AuthState>
      </div>

      <!-- Menu plegado -->
      <div class="xl:hidden">
        <UButton :aria-label="$t('labels.aria.menu')" variant="link" color="neutral"
          :class="loggedIn
            ? 'p-1 pr-2 gap-1.5 border border-neutral-300 rounded-full hover:cursor-pointer bg-gray-100 shadow-xs'
            : (isTransparent ? 'text-white' : 'text-neutral-900')"
          @click="() => { open = true }">
          <UAvatar v-if="loggedIn" :src="(user as OAuthUser)?.img"
            :alt="`${(user as OAuthUser)?.name || ''} ${(user as OAuthUser)?.surname || ''}`"
            :icon="!(user as OAuthUser)?.name ? 'i-lucide-user-circle' : undefined" size="2xs" :ui="{
              root: 'bg-byhours-gradient text-[8px]',
              fallback: 'text-white',
              icon: 'text-white text-sm',
            }" />
          <UIcon name="i-lucide-menu" class="size-5" :class="loggedIn ? 'text-neutral-800' : undefined" />
        </UButton>

        <UModal fullscreen :transition="false" v-model:open="open">
          <template #title>
            <div class="px-1 text-black">
              <NuxtLink :aria-label="$t('labels.aria.home')" @click="open = false; navigateTo(`/${locale}`)">
                <Logo />
              </NuxtLink>
            </div>
          </template>
          <template #body>
            <div class="px-2">

              <UNavigationMenu :key="locale" color="neutral" orientation="vertical" content-orientation="vertical"
                :items="items" class="w-full justify-center" :ui="{
                  list: 'gap-2',
                  link: 'text-sm text-toned py-2',
                  content: 'w-fit'
                }">
                <template #destinos-content="{ item }: { item: NavigationMenuItem }">
                  {{ item }}
                </template>
              </UNavigationMenu>

              <USeparator class="py-4" />

              <!-- Idioma y Moneda -->
              <div class="flex flex-col gap-2 pb-4">

                <!-- Idioma -->
                <div>
                  <p class="text-neutral-500 text-sm mb-1">{{ t('strapi.global.header.tituloIdioma') }}</p>
                  <LocaleSelector class="w-full" size="xl" />
                </div>

                <!-- Moneda -->
                <div>
                  <p class="text-neutral-500 text-sm mb-1">{{ t('strapi.global.header.tituloMoneda') }}</p>
                  <div>
                    <USelect class="w-full" size="xl" v-model="currency" :items="currencies" labelKey="id"
                      value-key="id" />
                  </div>
                </div>
              </div>

              <USeparator class="py-2" />

              <!-- Sesión -->
              <AuthState v-slot="{ user, loggedIn }">
                <div v-if="loggedIn" class="flex flex-col space-y-2 pt-1">
                  <UButton dynamic variant="subtle" color="neutral" block
                    :to="`/${locale}/user-zone/profile`"
                    class="py-2 px-3 pr-4 h-auto justify-start gap-3"
                    @click="() => { open = false }">
                    <UAvatar :src="(user as OAuthUser)?.img"
                      :alt="`${(user as OAuthUser)?.name || ''} ${(user as OAuthUser)?.surname || ''}`"
                      :icon="!(user as OAuthUser)?.name ? 'i-lucide-user-circle' : undefined" size="sm" :ui="{
                        root: 'bg-byhours-gradient text-xs',
                        fallback: 'text-white',
                        icon: 'text-white text-xl',
                      }" />
                    <span class="font-medium">{{ t('labels.user_zone.menu.perfil') }}</span>
                  </UButton>
                  <UButton @click="logoutAndCloseModal" :label="t('labels.login.dropdown.cerrar')"
                    icon="i-lucide-log-out" variant="ghost" color="neutral" />
                </div>
                <UButton v-else dynamic block :to="`/${locale}/login`" :aria-label="$t('labels.aria.usuario')"
                  icon="i-lucide-user-circle" :label="$t('buttons.login')" color="neutral" class="mt-2" size="lg" />
              </AuthState>
            </div>
          </template>
        </UModal>
      </div>
    </div>
  </header>
</template>